import type { FC, ReactNode } from 'react'
import type { ToolCategory } from '@/constants/tools'

import { useMemo, useState } from 'react'
import { ArrowRight, Lock, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import SiteHeader from '@/components/landing/site-header'
import SiteFooter from '@/components/landing/site-footer'
import { TOOLS } from '@/constants/tools'

type Filter = 'All' | ToolCategory

const FILTERS: Filter[] = ['All', 'Images', 'PDFs']

const STARTERS = [
    {
        title: 'A form says "under 500KB"',
        text: 'Start with the compressor. File size is how much storage a file uses.',
        href: '/compress-image?size=500&unit=KB',
        cta: 'Compress to 500KB'
    },
    {
        title: 'A listing says "1080 × 1080"',
        text: 'Start with resizing or a square crop. Dimensions describe the pixels in the image.',
        href: '/resize-image?w=1080&h=1080',
        cta: 'Resize to 1080 × 1080'
    }
]

const ToolsPage: FC = (): ReactNode => {
    const [filter, setFilter] = useState<Filter>('All')
    const [query, setQuery] = useState('')

    const visible = useMemo(() => {
        const term = query.trim().toLowerCase()
        return TOOLS.filter((tool) => (
            (filter === 'All' || tool.category === filter)
            && (!term || `${tool.name} ${tool.short} ${tool.description}`.toLowerCase().includes(term))
        ))
    }, [filter, query])

    return (
        <Layout>
            <Seo title='Free image tools' description='Free online image tools: compress to a target size, resize, crop, convert HEIC to JPG, remove metadata and turn photos into a PDF. No account, and files stay in your browser.' />

            <div className='min-h-screen bg-[#060b14] text-slate-200'>
                <SiteHeader />

                <section id='hero' className='relative overflow-hidden'>
                    <div className='pointer-events-none absolute -top-40 left-1/2 h-[480px] w-[900px] -translate-x-1/2 rounded-full bg-cyan-500/10 blur-3xl' />
                    <div className='relative mx-auto max-w-6xl px-6 pt-20 pb-12 md:pt-28'>
                        <p className='font-mono text-xs tracking-[0.2em] text-cyan-300 uppercase'>Free online image tools</p>
                        <h1 className='mt-5 max-w-4xl text-4xl leading-[1.05] font-extrabold tracking-tight text-white md:text-6xl'>
                            That file requirement? <span className='font-serif-accent font-normal text-amber-300'>You can work with it.</span>
                        </h1>
                        <p className='mt-6 max-w-2xl text-lg leading-relaxed text-slate-400'>
                            Make an image smaller, get the dimensions right, or change its format.
                        </p>
                        <p className='mt-4 inline-flex items-center gap-2 text-sm text-slate-400'>
                            <Lock className='size-4 text-cyan-300' /> These tools work in your browser. No account, and your files are never uploaded.
                        </p>
                    </div>
                </section>

                <section id='tools' className='mx-auto max-w-6xl px-6 pb-20'>
                    <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
                        <h2 className='text-2xl font-extrabold text-white'>Choose your task</h2>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
                            <div className='flex gap-2' role='group' aria-label='Filter tools'>
                                {FILTERS.map((item) => (
                                    <button
                                        key={item}
                                        type='button'
                                        aria-pressed={filter === item}
                                        onClick={() => setFilter(item)}
                                        className={cn(
                                            'rounded-full border px-4 py-2 text-sm font-medium transition',
                                            filter === item ? 'border-cyan-300 bg-cyan-300/10 text-cyan-200' : 'border-white/10 text-slate-400 hover:border-white/30'
                                        )}
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                            <label htmlFor='tool-search' className='sr-only'>Find a tool</label>
                            <div className='relative'>
                                <Search className='pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-slate-500' />
                                <input
                                    id='tool-search'
                                    type='search'
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder='Find a tool'
                                    className='w-full rounded-xl border border-white/10 bg-[#0a1424] py-2.5 pr-4 pl-10 text-sm text-white placeholder:text-slate-500 focus:border-cyan-300/60 focus:outline-none focus:ring-2 focus:ring-cyan-300/20 sm:w-56'
                                />
                            </div>
                        </div>
                    </div>

                    <div className='mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {visible.map((tool) => {
                            const Icon = tool.icon
                            return (
                                <a key={tool.slug} href={`/${tool.slug}`} className='group flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 transition hover:border-cyan-300/40 hover:bg-white/[0.04]'>
                                    <div className='flex items-center justify-between'>
                                        <span className='rounded-xl bg-cyan-400/10 p-2.5 text-cyan-300'><Icon className='size-5' /></span>
                                        <span className='text-xs text-slate-500'>{tool.category}</span>
                                    </div>
                                    <h3 className='mt-5 text-lg font-bold text-white'>{tool.name}</h3>
                                    <p className='mt-1 text-sm text-slate-400'>{tool.short}</p>
                                    <span className='mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-cyan-300'>
                                        Open tool <ArrowRight className='size-4 transition group-hover:translate-x-0.5' />
                                    </span>
                                </a>
                            )
                        })}
                    </div>
                    {visible.length === 0 && (
                        <p className='mt-8 text-sm text-slate-400'>
                            No tool matches that search. <button type='button' onClick={() => { setQuery(''); setFilter('All') }} className='text-cyan-300 hover:underline'>Show all tools</button>
                        </p>
                    )}
                </section>

                <section id='start-here' className='border-y border-white/10 bg-[#081120]'>
                    <div className='mx-auto max-w-6xl px-6 py-20'>
                        <p className='font-mono text-xs tracking-[0.15em] text-cyan-300 uppercase'>Not sure where to start?</p>
                        <h2 className='mt-4 text-2xl font-extrabold text-white md:text-3xl'>File size and image size are different.</h2>
                        <div className='mt-10 grid gap-6 md:grid-cols-2'>
                            {STARTERS.map((item) => (
                                <article key={item.href} className='flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8'>
                                    <h3 className='text-lg font-bold text-white'>{item.title}</h3>
                                    <p className='mt-2 text-sm leading-relaxed text-slate-400'>{item.text}</p>
                                    <a href={item.href} className='mt-auto inline-flex items-center gap-2 pt-6 text-sm font-semibold text-cyan-300 hover:underline'>
                                        {item.cta} <ArrowRight className='size-4' />
                                    </a>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id='guides-cta' className='mx-auto max-w-6xl px-6 py-20'>
                    <div className='flex flex-col items-start justify-between gap-6 md:flex-row md:items-center'>
                        <div>
                            <h2 className='text-2xl font-extrabold text-white'>Want to understand the choices?</h2>
                            <p className='mt-2 max-w-xl text-slate-400'>Short guides on upload limits, dimensions, formats and hidden photo details.</p>
                        </div>
                        <a href='/guides' className='inline-flex shrink-0 items-center gap-2 rounded-xl border border-white/15 px-5 py-3 text-sm font-bold text-white transition hover:border-cyan-300/50'>
                            Read the guides <ArrowRight className='size-4' />
                        </a>
                    </div>
                </section>

                <SiteFooter />
            </div>
        </Layout>
    )
}

export default ToolsPage
